/**
 * Payments — Services / Marketplace tabs.
 * One "Payments" destination under Finance, split by domain.
 */
import { useMemo, useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/common';
import { usePermissions } from '../../hooks/usePermissions';
import PaymentsListPage from './PaymentsListPage';
import { MarketplacePaymentsListPage } from '../marketplace';

const PaymentsTabsPage = () => {
  const { t } = useTranslation();
  const { hasPermission, isSuperUser } = usePermissions();
  const su = isSuperUser();

  const tabs = useMemo(() => {
    const list: { key: 'services' | 'marketplace'; label: string }[] = [];
    if (su || hasPermission('view_payments')) list.push({ key: 'services', label: t('payments.tabs.services') });
    if (su || hasPermission('view_marketplace')) list.push({ key: 'marketplace', label: t('payments.tabs.marketplace') });
    return list;
  }, [su, hasPermission, t]);

  const [tab, setTab] = useState(0);
  const active = tabs[tab]?.key ?? tabs[0]?.key;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('payments.title')}
        subtitle={t('payments.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), path: '/' }, { label: t('payments.title') }]}
      />

      {tabs.length > 1 && (
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      )}

      {active === 'services' && <PaymentsListPage embedded />}
      {active === 'marketplace' && <MarketplacePaymentsListPage embedded />}
    </Box>
  );
};

export default PaymentsTabsPage;
